import { cloneDeep, round } from 'lodash'
import { HourSeriesResult } from '../../../building-energy-model/hour-series-calculator'
import {
  BemCorrectionFactorsData,
  ConsumptionEnergyType,
} from '../../../generated/graphql'
import { i18n } from '../../../i18n'
import {
  monthsSumSerie,
  yearHours,
  yearMonths,
} from '../calibration/calibration-util'
import {
  createChartSerie,
  CustomLineStyles,
  CustomSeries,
} from '../calibration/chart-util'
import { sum } from '../../../util/array-utility'

const DEFAULT_DHW_LINETYPE = CustomLineStyles.Solid
const DEFAULT_DHW_LINEWIDTH = 0.1

const calcSummaryHourlyChartData = (
  serieIds: string[],
  hourSeries: (number[] | undefined)[],
): CustomSeries[] => {
  const result: CustomSeries[] = []

  const hours = yearHours()

  for (let i = 0; i < hourSeries.length; i++) {
    const hourSerie = hourSeries[i]
    if (hourSerie) {
      const tempSerie = cloneDeep(hourSerie)

      for (let j = 0; j < tempSerie.length; j++) {
        tempSerie[j] = round(tempSerie[j] / 1000, 2)
      }

      result.push(
        createChartSerie(
          serieIds[i],
          hours,
          tempSerie,
          CustomLineStyles.Solid,
          2,
          false,
        ),
      )
    }
  }

  return result
}

const calcSummaryMonthlyChartData = (
  serieIds: string[],
  hourSeries: (number[] | undefined)[],
  year: number,
): CustomSeries[] => {
  const result: CustomSeries[] = []

  for (let i = 0; i < hourSeries.length; i++) {
    const hourSerie = hourSeries[i]
    if (hourSerie) {
      const tempSerie = cloneDeep(hourSerie)

      const monthValues = yearMonths()

      const monthSerie = monthsSumSerie(tempSerie, year)

      for (let j = 0; j < monthSerie.length; j++) {
        monthSerie[j] = round(monthSerie[j] / 1000, 2)
      }

      result.push(
        createChartSerie(
          serieIds[i],
          monthValues,
          monthSerie,
          DEFAULT_DHW_LINETYPE,
          DEFAULT_DHW_LINEWIDTH,
          true,
        ),
      )
    }
  }

  return result
}

const heatingSerieIds = () => [
  i18n.t('buildingEnergyModels.hourSeriesNames.dhwCirculation'),
  i18n.t('buildingEnergyModels.hourSeriesNames.dhwHeating'),
  i18n.t('buildingEnergyModels.hourSeriesNames.spaceHeating'),
  i18n.t('buildingEnergyModels.hourSeriesNames.ventilationHeating'),
]

const heatingHourSeries = (hourSeries: HourSeriesResult[]) => [
  hourSeries.find(
    (s) =>
      s.consumptionEnergyType === ConsumptionEnergyType.DhwCirculationHeating,
  )?.hourSerie,
  hourSeries.find(
    (s) => s.consumptionEnergyType === ConsumptionEnergyType.DhwHeating,
  )?.hourSerie,
  hourSeries.find(
    (s) => s.consumptionEnergyType === ConsumptionEnergyType.SpaceHeating,
  )?.hourSerie,
  hourSeries.find(
    (s) => s.consumptionEnergyType === ConsumptionEnergyType.VentilationHeating,
  )?.hourSerie,
]

export const calcSummaryHourlyHeatingChartData = (
  hourSeries: HourSeriesResult[],
): CustomSeries[] =>
  calcSummaryHourlyChartData(heatingSerieIds(), heatingHourSeries(hourSeries))

export const calcSummaryMonthlyHeatingChartData = (
  hourSeries: HourSeriesResult[],
  year: number,
): CustomSeries[] =>
  calcSummaryMonthlyChartData(
    heatingSerieIds(),
    heatingHourSeries(hourSeries),
    year,
  )

export const calcSummaryMonthlyElectricityChartData = (
  hourSeries: HourSeriesResult[],
  year: number,
): CustomSeries[] => {
  const hourSerieIds = [
    i18n.t('buildingEnergyModels.hourSeriesNames.elVentilation'),
    i18n.t('buildingEnergyModels.hourSeriesNames.elLighting'),
    i18n.t('buildingEnergyModels.hourSeriesNames.elAppliances'),
    i18n.t('buildingEnergyModels.hourSeriesNames.elBaseLoad'),
  ]

  const electricityHourSeries = [
    hourSeries.find(
      (s) =>
        s.consumptionEnergyType ===
        ConsumptionEnergyType.VentilationElectricity,
    )?.hourSerie,
    hourSeries.find(
      (s) =>
        s.consumptionEnergyType === ConsumptionEnergyType.LightingElectricity,
    )?.hourSerie,
    hourSeries.find(
      (s) =>
        s.consumptionEnergyType === ConsumptionEnergyType.AppliancesElectricity,
    )?.hourSerie,
    hourSeries.find(
      (s) =>
        s.consumptionEnergyType === ConsumptionEnergyType.BaseLoadElectricity,
    )?.hourSerie,
  ]

  const result = calcSummaryMonthlyChartData(
    hourSerieIds,
    electricityHourSeries,
    year,
  )

  return result
}

export const calcSummaryHourlyCoolingChartData = (
  hourSeries: HourSeriesResult[],
): CustomSeries[] => {
  const hourSerieIds = [
    i18n.t('buildingEnergyModels.hourSeriesNames.spaceCooling'),
    i18n.t('buildingEnergyModels.hourSeriesNames.ahuCooling'),
    i18n.t('buildingEnergyModels.hourSeriesNames.systemCooling'),
  ]

  const coolingHourSeries = [
    hourSeries.find(
      (s) => s.consumptionEnergyType === ConsumptionEnergyType.SpaceCooling,
    )?.hourSerie,
    hourSeries.find(
      (s) => s.consumptionEnergyType === ConsumptionEnergyType.AhuCooling,
    )?.hourSerie,
    hourSeries.find(
      (s) => s.consumptionEnergyType === ConsumptionEnergyType.SystemCooling,
    )?.hourSerie,
  ]

  return calcSummaryHourlyChartData(hourSerieIds, coolingHourSeries)
}

export const calcSerieSum = (serie: number[]): number => sum(serie)

export const initialCorrectionFactorsData = (): BemCorrectionFactorsData[] => {
  const factors: BemCorrectionFactorsData[] = []

  Object.values(ConsumptionEnergyType).forEach((value) => {
    factors.push({
      energyType: value as ConsumptionEnergyType,
      correctionFactor: 1,
      energyFactor: 1,
    })
  })

  return factors
}

export type BemCorrectionFactorsDataFormValues = {
  energyType: ConsumptionEnergyType
  correctionFactor: string
  energyFactor: string
}

export type FactorFormValues = {
  factorData: BemCorrectionFactorsDataFormValues[]
}

export const mapInputToFactorFormValues = (
  factors: BemCorrectionFactorsData[],
): FactorFormValues => {
  const values = factors.map(
    (f) =>
      ({
        energyType: f.energyType,
        correctionFactor: String(f.correctionFactor).replace('.', ','),
        energyFactor: String(f.energyFactor).replace('.', ','),
      } as BemCorrectionFactorsDataFormValues),
  )

  return { factorData: values }
}
